/**
<com-calendar> is an element that renders a page allowing the user to see linked tickets and passes.

@example
<com-calendar>
</com-calendar>

@dependencies
dprd-icon
*/
/*
  FIXME(polymer-modulizer): the above comments were extracted
  from HTML and may be out of place here. Review them and
  then delete this comment!
*/
import { PolymerElement, html } from '@polymer/polymer';
import { timeOut } from '@polymer/polymer/lib/utils/async.js';
import './style/com-calendar-style.js';
import './style/com-calendar-basic-style.js';

import '@polymer/polymer/lib/elements/dom-if.js';
import '@polymer/polymer/lib/elements/dom-repeat.js';
import './com-calendar-base.js';
import './com-calendar-date.js';
import './com-calendar-legend.js';
import '@com/price';
import '@com/html';

import { COMAdaptiveBehavior } from '@com/adaptive';

import { COMCalendarBehavior, CALENDAR_TYPE } from './com-calendar-behavior.js';
import { COMCalendarDateFormatter } from './lib/com-calendar-date-formatter.js';

import { GestureEventListeners } from '@polymer/polymer/lib/mixins/gesture-event-listeners.js';
import { afterNextRender } from '@polymer/polymer/lib/utils/render-status.js';


/**
 * `com-calendar`
 *
 * @customElement
 * @polymer
 * @demo demo/index.html
 */
class COMCalendar extends COMCalendarBehavior(GestureEventListeners(COMAdaptiveBehavior(PolymerElement))) {
  static get template() {
    return html`
    <style include="com-theme"></style>
    <style include="com-calendar-style"></style>
    <style include="com-calendar-basic-style"></style>
    <!-- Scoped styles for this element -->

    <com-price id="price" hide-decimals hide-currency presentation="commerce" hidden></com-price>

      <!-- date header and navigation controls -->

      <!-- Selected dates overview section on top -->
      <template is="dom-if" if="[[!showSelectedDatesOverviewBelowCalendar]]">
        <template is="dom-if" restamp="" if="[[_shouldShowOverview(maxAllowed, showSelectedDatesOverview)]]">
          <slot name="selectedDatesOverview" slot="selectedDatesOverviewContent"></slot>
        </template>
      </template>

      <template is="dom-if" if="[[showErrorWarning]]">
        <slot name="warningsAndErrors" slot="warningsAndErrorsContent"></slot>
      </template>

      <template is="dom-if" if="[[!collapseCalendar]]">
        <template is="dom-if" if="[[calendarLegendOnTop]]">
          <com-calendar-legend
            class="calendarLegend"
            is-variable-parks="[[isVariableParks]]"
            date-type-map="[[dateTypeMap]]"
            price-view="[[priceView]]"
            legend-aria-label="[[legendAriaLabel]]"
          ></com-calendar-legend>
        </template>
        <div id="calendarWrapper">
        <div class="comCalendar">
          <div class="calendarNav" hidden\$="[[!_hasNavigationArrows(adaptive, useGridDisplayStyle)]]">
            <com-calendar-button id="prevArrow" class="calendarNavBtn" value="&#58125;" aria-label="[[ariaContent.navigationPrevMonth]]" on-tap="_prevHandler" on-mousedown="_preventFocus" hidden\$="[[disablePrevArrow]]"></com-calendar-button>
            <com-calendar-button id="nextArrow" class="calendarNavBtn" value="&#58124;" aria-label="[[ariaContent.navigationNextMonth]]" on-tap="_nextHandler" on-mousedown="_preventFocus" hidden\$="[[disableNextArrow]]"></com-calendar-button>
          </div>
          <template is="dom-repeat" items="[[_getCalendarDates(currentDate, adaptive, dates)]]" index-as="monthIndex">
            <div class="reservationCalendar">
              <com-calendar-base
                id="calendar[[monthIndex]]"
                class="calendarItems"
                allowed-day-range="[[allowedDayRange]]"
                max-allowed="[[maxAllowed]]"
                start-date="[[item]]"
                end-date="[[_getEndDate(dates, endDate, monthsToDisplay, displayStrictNumMonths)]]"
                navigation="[[_getCalendarNavigation(adaptive, useGridDisplayStyle)]]"
                presentation="compact"
                disable-prev-arrow="[[_isNavDisabled(monthIndex, 'prev', adaptive, monthsToDisplay, useGridDisplayStyle)]]"
                disable-next-arrow="[[_isNavDisabled(monthIndex, 'next', adaptive, monthsToDisplay, useGridDisplayStyle)]]"
                disable-selection="[[disableSelections]]"
                aria-content="[[ariaContent]]"
                display-content="[[displayContent]]"
                allow-disabled-focus="[[allowDisabledFocus]]"
                only-enable-slotted
                selected-dates="{{selectedDates}}"
                selectable-end-date="{{selectableEndDate}}"
                available-end-date="[[availableEndDate]]"
                disable-current-date-reset="[[disableCurrentDateReset]]"
                animate="[[animate]]"
                enable-track="[[enableTrack]]"
                enable-swipeable-header="[[enableSwipeableHeader]]"
                propogate-nav-events
                prev-start-date="[[_getStartDate(monthIndex, startDate, calendarDates)]]"
                next-month-offset="[[_getNextMonthOffset(adaptive, monthsToDisplay)]]"
                weekday-offset="[[weekdayOffset]]"
                drawer-enable="[[drawerEnable]]"
                disable-additional-aria-date-label="[[priceView]]"
                enable-fuse="[[enableFuse]]"
                fuse-dates="[[_getFuseDates(calendarDates)]]"
                clean
                available-dates="[[_getAvailableDatesAllMonths(_availableDates)]]"
                use-grid-display-style$="[[useGridDisplayStyle]]"
                use-same-calendar-height$="[[_shouldUseSameCalendarHeight(adaptive, monthsToDisplay, useSameCalendarHeight)]]"
                enable-nav-to-disabled-dates$="[[enableNavToDisabledDates]]"
                enable-nav-to-past-dates$="[[enableNavToPastDates]]"
                calendar-aria-live-setting="[[calendarAriaLiveSetting]]"
                reset-range-on-max-reached="[[dateRangePicker]]",
                month-index=[[monthIndex]]
                no-weekdays-border="[[disableWeekdaysBorder]]"
              >
                <template is="dom-repeat" items="[[_getAvailableDates(_availableDates, item)]]" as="dateItem" restamp="" on-dom-change="_dispatchAvailLoad">
                  <template is="dom-if" if="[[useSimpleDateElement]]">
                    <div
                      slot\$="[[dateItem.date]]"
                      class\$="simple-date-container [[dateItem.dateType.class]]"
                      disabled\$="[[dateItem.disabled]]"
                      aria-label="overridden?"
                      date="[[dateItem.date]]"
                      past="[[isPastDate(dateItem.date, startDate)]]"
                      additional-aria-label="[[_getAdditionalDateLabel(dateItem, priceView)]]"
                    >
                      <div class\$="simple-date-number [[_getAdditionalClass(goodToGoEnabled, 'simple-date-number-flex')]]">
                          {{_formatDateFromString(dateItem.date)}}
                          <svg aria-hidden="true" class="slash" disabled=""><line x1="0" y1="20%" x2="100%" y2="80%"></line></svg>
                      </div>
                      <div class="simple-date-indicator-container">
                        <template is="dom-if" if="[[dateItem.dateType.isIcon]]">
                          <template is="dom-if" if="[[dateItem.dateType.isOverlappingIcon]]">
                            <div class="overlapping">
                              <div class="overlappingInner">
                                <com-icon class\$="dateTypeIndicatorIcon [[dateItem.dateType.firstClass]] firstIcon" value="[[dateItem.dateType.firstIcon]]"></com-icon>
                                <com-icon class\$="dateTypeIndicatorIcon [[dateItem.dateType.secondClass]] secondIcon" value="[[dateItem.dateType.secondIcon]]"></com-icon>
                              </div>
                            </div>
                          </template>
                          <template is="dom-if" if="[[!dateItem.dateType.isOverlappingIcon]]">
                            <com-icon class\$="dateTypeIndicatorIcon [[dateItem.dateType.class]]" value="[[dateItem.dateType.content]]"></com-icon>
                          </template>
                        </template>
                        <template is="dom-if" if="[[_isContentAvailable(dateItem.dateType)]]">
                          <div class\$="dateTypeIndicator [[dateItem.dateType.class]]" inner-h-t-m-l="[[dateItem.dateType.content]]"></div>
                        </template>
                      </div>
                    </div>
                  </template>
                  <template is="dom-if" if="[[!useSimpleDateElement]]">
                    <com-calendar-date
                      presentation="compact"
                      price-view="[[priceView]]"
                      allow-disabled-focus="[[_getAllowDisabledFocus(dateItem.disabled, allowDisabledFocus, dateItem.unavailable, allowUnavailableSelection)]]"
                      additional-aria-label="[[_getAdditionalDateLabel(dateItem, priceView)]]"
                      allow-unavailable-selection="[[allowUnavailableSelection]]"
                      aria-live="[[calendarAriaLiveSetting]]"
                      unavailable="[[dateItem.unavailable]]"
                      disabled="[[dateItem.disabled]]"
                      class\$="[[dateItem.dateType.class]]"
                      slot\$="[[dateItem.date]]"
                      disable-user-interaction$="[[disableUserInteraction]]"
                      adaptive="[[adaptive]]"
                      slash-on-blocked-only$="[[slashOnBlockedOnly]]"
                      enable-nav-to-disabled-dates$="[[enableNavToDisabledDates]]"
                      enable-nav-to-past-dates$="[[enableNavToPastDates]]"
                      use-simple-date-element="[[useSimpleDateElement]]"
                    >
                      <div slot="dateTypeIndicator">
                        <template is="dom-if" if="[[!priceView]]">
                          <template is="dom-if" if="[[dateItem.dateType.isIcon]]">

                            <template is="dom-if" if="[[dateItem.dateType.isOverlappingIcon]]">
                              <div class="overlapping">
                                <div class="overlappingInner">
                                  <com-icon class\$="dateTypeIndicatorIcon [[dateItem.dateType.firstClass]] firstIcon" value="[[dateItem.dateType.firstIcon]]"></com-icon>
                                  <com-icon class\$="dateTypeIndicatorIcon [[dateItem.dateType.secondClass]] secondIcon" value="[[dateItem.dateType.secondIcon]]"></com-icon>
                                </div>
                              </div>
                            </template>
                            <template is="dom-if" if="[[!dateItem.dateType.isOverlappingIcon]]">
                              <com-icon class\$="dateTypeIndicatorIcon [[dateItem.dateType.class]]" value="[[dateItem.dateType.content]]"></com-icon>
                            </template>

                          </template>
                          <template is="dom-if" if="[[!dateItem.dateType.isIcon]]">
                            <div class\$="dateTypeIndicator [[dateItem.dateType.class]]" inner-h-t-m-l="[[dateItem.dateType.content]]"></div>
                          </template>
                        </template>
                        <template is="dom-if" if="[[_allowDateIndicatorOnPriceView(dateItem, priceView, allowDateIndicatorOnPriceView)]]">
                          <com-icon class\$="dateTypeIndicatorIcon [[dateItem.dateType.class]]" value="[[dateItem.dateType.content]]"></com-icon>
                        </template>
                        <template is="dom-if" if="[[_showPrice(dateItem, priceView)]]">
                          <div class="dateTypeIndicator all">
                            <com-price presentation="inline" hide-currency hide-decimals value="[[dateItem.price]]"></com-price>
                          </div>
                        </template>
                      </div>
                    </com-calendar-date>
                  </template>
                </template>

                <!-- end message -->
                <template is="dom-if" if="[[displayContent.endMessage]]">
                  <div id="endMessage" slot="endMessage">
                    <com-html value="[[displayContent.endMessage]]"><com-html>
                  </div>
                </template>

                <!-- drawer -->
                <template is="dom-if" if="[[_showDrawer(displayContent.drawer, drawerEnable, monthIndex)]]">
                  <div id="drawer" slot="drawer">
                    <slot name="drawerContent">
                    </slot>
                  </div>
                </template>
              </com-calendar-base>
            </div>
            <div class="break" id="[[_getFlexBreakValue(useGridDisplayStyle, monthsPerDisplayRow, monthIndex)]]"></div>
          </template>
        </div>
        <template is="dom-if" if="[[!moveLegendFromDefaultPosition]]">
          <com-calendar-legend
            class="calendarLegend"
            is-variable-parks="[[isVariableParks]]"
            date-type-map="[[dateTypeMap]]"
            price-view="[[priceView]]"
            legend-aria-label="[[legendAriaLabel]]"
          ></com-calendar-legend>
        </template>
      </template>
      </div>
      <!-- Selected dates overview section below calendar -->
      <template is="dom-if" if="[[showSelectedDatesOverviewBelowCalendar]]">
        <template is="dom-if" restamp="" if="[[_shouldShowOverview(maxAllowed, showSelectedDatesOverview)]]">
          <slot name="selectedDatesOverview" slot="selectedDatesOverviewContent"></slot>
        </template>
      </template>
`;
  }

  static get is() { return 'com-calendar'; }

  static get properties() {
    return {

      /**
       * @desc Selected Dates
       * @type {String}
       */
      selectedDates: {
        type: Array,
        value: []
      },

      /**
       * @desc Selected Date
       * @type {String}
       */
      selectedDate: {
        type: String,
        observer: '_onSelectedDateChanged'
      },

      /**
       * @desc Selectable End Dates
       * @type {String}
       */
      selectableEndDate: {
        type: String,
        value: ''
      },

      dates: Array,

      /**
       * @desc current date
       * @type {String}
       */
      currentDate: {
        type: String,
        notify: true
      },

      /**
       * @desc current date
       * @type {String}
       */
      hideYear: {
        type: Boolean,
        notify: true
      },

      /**
       * @desc allow date indicator on price view
       * @type {Boolean}
       */
      allowDateIndicatorOnPriceView: {
        type: Boolean,
        value: false
      },

      /**
       * @desc current date for mobile calendar
       * @type {String}
       */
      mobileCurrentDate: {
        type: String,
        notify: true
      },

      /**
       * @desc start dates for calendars
       * @type {String[]}
       */
      _componentInit: {
        type: Boolean,
        value: true
      },

      disableSelections: {
        type: Boolean,
        value: false
      },

      disabled: {
        type: Boolean,
        value: false
      },

      /**
       * @desc animate calendar transition
       * @type {Boolean}
       */
      animate: {
        type: Boolean,
        value: false
      },

      /**
       * @desc Flag to enable track gesture in months
       * @type {Boolean}
       */
      enableTrack: {
        type: Boolean,
        value: false
      },

      /**
       * @desc Flag to enable fuse
       * @type {Boolean}
       */
      enableFuse: {
        type: Boolean,
        value: false
      },

      /**
       * @desc enable header for swipe
       * @type {Boolean}
       */
      enableSwipeableHeader: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },

      /**
       * @desc allow focus on disabled dates to allow guests to read info only calendars
       * @type {Boolean}
       */
      allowDisabledFocus: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },

      /**
       * @desc allow selection of unavailable dates
       */
      allowUnavailableSelection: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * @desc Flag to indicate if calendar should enable the disable dates with accessibility label
       * @type {Boolean}
       */
      enableNavToDisabledDates: {
        type: Boolean,
        value: false
      },
      /**
       * @desc Flag to indicate if calendar should enable the past dates with accessibility label
       * @type {Boolean}
       */
      enableNavToPastDates: {
        type: Boolean,
        value: false
      },

      /**
       * @desc start dates for calendars
       * @type {String[]}
       */
      _calendarInitialized: Object,

      /**
       * @desc start dates for calendars
       * @type {String[]}
       */
      startDate: {
        type: String,
        value: function () {
          return this.getStringFromDate(this.getToday());
        }
      },

      calendarDates: {
        type: Object
      },

      /**
       * @desc Error key to be used to display error message
       * @type {String}
       */
      _errorKey: String,

      /**
       * @desc Last eligible date to reserve
       * @type {String}
       */
      _selectableEndDate: String,

      /**
       * @desc park Id map
       * @type {Array}
       */
      _parkIdMap: Array,

      /**
       * @desc Default calendar date type
       * @type {String}
       */
      _defaultDateType: {
        type: String,
        value: 'blockedOut'
      },
      /**
       * @desc Get part type for aria labels
       * @type {Object}
       */
      _parkTypeAriaLabels: Object,

      /**
       * @desc total number of available months to display in calendar
       * @type {String}
       */
      monthsToDisplay: {
        type: Number,
        value: 2
      },

      /**
       * @desc Toggle value for ApBlockoutUseLocaleDateFormat
       * @type {String}
       */
      _useLocaleDateFormat: {
        type: Boolean,
        value: false
      },

      _availableDates: {
        type: Object
      },

      allowedDayRange: {
        type: Number,
        value: 14
      },

      maxAllowed: {
        type: Number,
        value: 1
      },

      /**
       * @desc Determine if we should show selected dates overview below calendar.
       */
      showSelectedDatesOverviewBelowCalendar: {
        type: Boolean,
        value: false
      },

      _totalCalendarLoaded: {
        type: Number,
        value: 0
      },

      weekdayOffset: {
        type: Number,
        value: 0
      },

      priceView: {
        type: Boolean,
        value: false,
        observer: '_refreshCalendarInstances'
      },
      /**
       * @desc drawer enable state
       * @type {Boolean}
       */
      drawerEnable: {
        type: Boolean,
        value: false
      },
      /**
       * @desc collapse calendar state
       * @type {Boolean}
       */
      collapseCalendar: {
        type: Boolean,
        value: false
      },
      /**
       * @desc display content
       * @type {Object}
       */
      displayContent: Object,

      dateType: {
        type: String,
        value: 'avail'
      },
      /**
       * @desc Toggles on/off the errors and warnings message
       * @type {Boolean}
       */
      showErrorWarning: {
        type: Boolean,
        value: true
      },
      ariaContent: {
        type: Object,
        value: {
          dateDefault: '',
          dateDisabled: 'not selectable',
          dateSelected: 'selected',
          dateSelectable: 'in selectable range',
          navigationNextMonth: 'next month',
          navigationPrevMonth: 'previous month',
          navigationDisabled: 'not selectable',
          dateUnavailable: 'Park Reservations unavailable, disabled',
          blockedOut: 'Blocked Out',
          noInformationAvailable: 'no information available'
        }
      },

      dateTypeMap: {
        type: Object,
        value: {
          all: {
            class: 'all',
            content: '',
          },
          none: {
            class: 'none',
            content: '',
          }
        }
      },

      /**
       * @desc Determine if we have a variable quantity of parks.
       * @type {Boolean}
       */
      isVariableParks: {
        type: Boolean,
        value: false
      },
      /**
       * @desc Determine if we should show calendar legends on top.
       * @type {Boolean}
       */
      calendarLegendOnTop: {
        type: Boolean,
        value: false,
        observer: '_setCalendarLegendPosition'
      },
      /**
       * @desc Determine if we should use the grid display
       * @type {Boolean}
       */
      useGridDisplayStyle: {
        type: Boolean,
        value: false
      },
      /**
       * @desc Determine if we should show calendars with same amount of rows for side by side calendars.
       * @type {Boolean}
       */
      useSameCalendarHeight: {
        type: Boolean,
        value: false
      },
      /**
       * @desc If using grid display, number of months in a grid row
       * @type {Number}
       */
      monthsPerDisplayRow: {
        type: Number,
        default: 0
      },
      /**
       * @desc Determine if legend should be shown in default position (bottom, below the calendar months)
       * @type {Boolean}
       */
      moveLegendFromDefaultPosition: {
        type: Boolean,
        value: false
      },
      /**
       * @desc Flag to indicate if calendar should be interactive
       * @type {Boolean}
       */
      disableUserInteraction: {
        type: Boolean,
        value: false
      },
      /**
       * @desc Flag to indicate if slash should be shown only for blocked date types
       * @type {Boolean}
       */
      slashOnBlockedOnly: {
        type: Boolean,
        value: false
      },
      /**
       * @desc aria-label attribute to set on legend container
       * @type {String}
       */
      legendAriaLabel: {
        type: String,
        value: 'Legend'
      },
      /**
       * @desc Flag to indicate if calendar should show exactly the number of months
       * specified in the monthsToDisplay attribute, despite what dates have been supplied
       * ex: show 12 months even if only 3 months of dates provided
       * @type {Boolean}
       */
      displayStrictNumMonths: {
        type: Boolean,
        value: false
      },
      /**
       * @desc Setting for the aria-live on calendar dates
       * available options: 'polite', 'assertive', 'off'
       * @type {String}
       */
      calendarAriaLiveSetting: {
        type: String,
        value: 'polite'
      },
      /**
       * @desc Flag to indicate if simple (non-interactive) date element should be used instead of com-calendar-date
       * (enables faster load of large calendars)
       * NOTE: The simple date element does NOT support price view at the moment
       * @type {Boolean}
       */
      useSimpleDateElement: {
        type: Boolean,
        value: false
      },

      /**
       * @desc Number of days to generate for date picker
       */
      dateRangePickerNumDays: {
        type: Number,
        value: 30
      },

      /**
       * @desc Validate in case we get an empty array from the service
       * @type {Boolean}
       */
      showEmptyMessageOnEmptyDates: {
        type: Boolean,
        value: false
      },

      /**
       * @desc Allow date range picker mode for current calendar
       *   - Generates available dates to select internally, using dateRangePickerNumDays
       */
      dateRangePicker: {
        type: Boolean,
        value: false
      },

      /**
       * @desc list of dates as strings to disable when using date range picker
       */
      dateRangeBlockedDates: Array,

      /**
       * @desc Current Date to load when calendar first loads
       * @type {String}
       */
      currentDateOnLoad: String,

      /**
       * @desc Flag to present the selected dates overview 
       * @type {Boolean}
       */
      showSelectedDatesOverview: Boolean,

      /**
       * @desc toggle to disable com-calendar same date propagation to prevent multiple calls
       * @type {Boolean}
       */
      disableSameDateEventPropagation: {
        type: Boolean,
        value: false
      },
      /**
       * @desc whether to display future dates as unblocked
       * @type {Boolean}
       */
      unblockFutureDates: {
        type: Boolean,
        value: false
      },
      /**
       * @desc good-to-go Toggle
       * @type {Boolean}
       */
      goodToGoEnabled: {
        type: Boolean,
        value: false
      },
      /**
       * @desc if calendar navigation is disabled
       * @type {Boolean}
       */
      disableNavigation: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * @desc if prev arrow is disabled/hidden
       * @type {Boolean}
       */
      disablePrevArrow: {
        type: Boolean,
        value: false
      },
      /**
       * @desc if next arrow is disabled/hidden
       * @type {Boolean}
       */
      disableNextArrow: {
        type: Boolean,
        value: false
      },
      /**
       * @desc toggle to set block dates style for non-available dates
       * @type {Boolean}
       */
      setCalendarStylesToBlocked: {
        type: Boolean,
        value: false
      },
      /**
       * @desc toggle to enabled border bottom on weekdays element
       * @type {Boolean}
       */
      disableWeekdaysBorder: {
        type: Boolean,
        value: false,
      },
    };
  }

  /**
   * @todo get the fuseDates from the availability calendar (calendarDates)
   * For testing purposes, to set a smaller range of fuse dates, return {start: '2020-11-24', end: '2020-12-24'};
   */
  _getFuseDates(calendarDates) {
    return calendarDates;
  }

  /**
   * @desc Check to see if content is available to render as date indicator
   * @param {*} dateType
   * @returns
   */
  _isContentAvailable(dateType) {
    return dateType && dateType.content && !dateType.isIcon;
  }

  ready() {
    super.ready();

    this._calendarInitialized = {
      mobile: false,
      desktop: {
        total: 0,
        initalized: false
      }
    };
    this._initCurrentDate();

    this.addEventListener('com-calendar.selection-updated', (event) => {
      /**
       * @todo evaluate if is possible to refactor this logic to use only one event for com-calendar.selectedDates
       */
      if (event.detail && event.detail.selectedDates) {
        /**
         * @todo wait until business decides on what needs to be done when clicking
         * a selected date off and current view no longer contains a selected date.
         * By now, if the selected date off is the same as first date on selectedDates,
         * navigate to the new first date on the selectedDates range. When clearing out
         * the selectedDates, stay on the same month.
         */

        // If multi day enabled and there are dates selected
        if (this.selectedDates && this.maxAllowed > 1 && this.selectedDates.length > 0 &&
          // and a date is being removed from the selection
          event.detail.selectedDates.length < this.selectedDates.length && event.detail.selectedDates[0] &&
          // and the removed date is equal to the first selectedDate
          event.detail.lastDateSelected && event.detail.lastDateSelected === this.selectedDates[0]) {
          let currentDate = this._getDateStringFromMonth(event.detail.selectedDates[0], 0);
          this.currentDate = currentDate;
        }

        if (!this.disableSameDateEventPropagation || this.selectedDates != event.detail.selectedDates) {
          this.selectedDates = event.detail.selectedDates;
          this.dispatchEvent(new CustomEvent('com-calendar.selectedDates', {
            bubbles: true,
            composed: true,
            detail: {
              selectedDates: this.selectedDates
            }
          }));
        }
      }

      this.selectedDate = this.selectedDates && this.selectedDates[0] ? this.selectedDates[0] : '';

      if (event.detail && event.detail.startDate) {
        this._refreshCalendarInstances();
      }
      this._toggleDrawerState(this.selectedDates);
    });

    this.addEventListener('com-calendar.navigation-change', event => {
      let currentDates = this._getCalendarDates(this.currentDate, this.adaptive, this.dates);

      if (currentDates.length > 1 && event.detail.direction === 'next') {
        this.currentDate = currentDates[1];
      } else {
        this.currentDate = event.detail.currentDate;
      }
      this._toggleDrawerState(this.selectedDates);

      this.dispatchEvent(new CustomEvent('com-calendar.navigation-changed', {
        bubbles: true,
        composed: true,
        detail: event.detail.direction
      }));
    });

    this.addEventListener('com-calendar.month-data-loaded', event => {
      this._totalCalendarLoaded++;

      if (
        this._showSmallCalendar(this.adaptive)
        || (
          !this._showSmallCalendar(this.adaptive)
          && this._totalCalendarLoaded === this.monthsToDisplay
        )
      ) {
        this._totalCalendarLoaded = 0;

        this._refreshCalendarInstances();

        let baseCalendars = this.shadowRoot.querySelectorAll('com-calendar-base');
      }
    });
  }

  static get observers() {
    return [
      '_initDateRangePicker(dateRangePicker, dateRangePickerNumDays, dateRangeBlockedDates)',
      '_initCalendarData(dates)',
      '_setNavigation(currentDate, startDate, endDate, disableNavigation, disablePrevArrow, disableNextArrow)'
    ];
  }

  /**
   * @desck Initialze calendar as date range picker mode
   * @param {*} dateRangePicker
   * @param {*} dateRangePickerNumDays
   * @param {*} dateRangeBlockedDates
   */
  _initDateRangePicker(dateRangePicker, dateRangePickerNumDays, dateRangeBlockedDates) {
    const blockedDatesMap = {};
    // create blocked dates map to filter against
    if (dateRangeBlockedDates && dateRangeBlockedDates.length > 0) {
      dateRangeBlockedDates.forEach(blockedDate => {
        blockedDatesMap[blockedDate] = true;
      });
    }
    if (dateRangePicker && dateRangePickerNumDays > 0) {
      const today = this.getToday();
      const dates = [];
      for (let i = 0; i < dateRangePickerNumDays; i++) {
        const currentDate = today.toISOString().replace(/T.*$/, '');

        // add date if not blocked out
        if (!blockedDatesMap[currentDate]) {
          dates.push({
            date: currentDate,
            facilities: [
              {
                facilityName: 'all',
                available: true
              }
            ]
          });
        }
        today.setDate(today.getDate() + 1);
      }
      this.dates = dates;
    }
  }

  _refreshCalendarInstances() {
    let baseCalendars = this.shadowRoot.querySelectorAll('com-calendar-base');
    if (baseCalendars) {
      baseCalendars.forEach(calendar => {
        calendar.refreshDateElements();
      });
      // we need to toggle the drawer state here
      // for persitence, in case there are selectedDates,
      // to display the drawer.
      this._toggleDrawerState(this.selectedDates);
    }
  }

  /**
   * @return {Boolean} true if the calendar is in single day mode
   */
  _isSingleDayMode() {
    return this.maxAllowed === 1;
  }

  /**
   * @desc Check if we need to show the selected dates overview
   * @param {Number} maxAllowed
   * @param {Boolean} showSelectedDatesOverview
   * @return {Boolean} true if the calendar is in multiday mode or the flag showSelectedDatesOverview is true
   */
  _shouldShowOverview(maxAllowed, showSelectedDatesOverview) {
    return maxAllowed > 1  || showSelectedDatesOverview;
  }

  /**
   * Check if we should allow an indicator on price view
   * @param {Object} dateItem
   * @param {Boolean} priceView
   * @return {Boolean}
   */
  _allowDateIndicatorOnPriceView(dateItem, priceView, allowDateIndicatorOnPriceView) {
    return priceView &&
      !this._showPrice(dateItem, priceView) &&
      dateItem.dateType.isIcon &&
      allowDateIndicatorOnPriceView;
  }

  _initCalendarData(dates) {

    let availableDates = {};

    let endDate = '';

    dates.forEach(element => {
      availableDates[element.date] = element;

      if (endDate === '' || endDate < element.date) {
        endDate = element.date;
      }
    });

    // Wait for the next render cycle to update availableDates.
    // This fixes https://myjira.disney.com/browse/CME-444 only present on 13.4 and lower.
    // We can get rid of this 'timeout.run()' wrapper when dropping the iOS 13 support.
    timeOut.run(() => {
      this._availableDates = availableDates;
      if (!this.selectedDates || this.selectedDates.length !== this.maxAllowed || this.enableFuse) {
        this.calendarDates = {
          start: this.startDate,
          end: endDate
        };

        // If client provided current date to load and it is greater than the currentDate, use it
        if (this.currentDateOnLoad && this.currentDateOnLoad > this.currentDate) {
          this.currentDate = this.currentDateOnLoad;
        } else if (this.startDate > this.currentDate) {
          // if current date is less than start date, set it start date so that
          // subsequent calendar can render next month's calendar properly
          this.currentDate = this.startDate;
        }
      } else {
        // logic to handle populating selected dates
        const firstSelectedDate = this.getDateObject(this.selectedDates[0]);
        // we need to reduce - 1 to allowedDayRange because the first selected date
        // count as the first date of the rage
        firstSelectedDate.setDate(firstSelectedDate.getDate() + (this.allowedDayRange - 1));
        this.calendarDates = {
          start: this.selectedDates[0],
          end: this.getStringFromDate(firstSelectedDate),
          preselectedDates: true
        };
        // In Variable parks we just have one selected date!, (the current date), That is not
        // necessary that we change it... If we do, the calendar always will show this month (WDWAP-585)
        this.currentDate = this.isVariableParks || this.unblockFutureDates ? this.currentDate : this.selectedDates[0];
      }

      this.availableEndDate = endDate;
    }, 1);
  }

  _getNextMonthOffset(adaptive, monthsToDisplay) {
    return !this._showSmallCalendar(adaptive) && monthsToDisplay > 2 ? 1 - monthsToDisplay : 0;
  }

  /**
   * Initialize current date on page load
   */
  _initCurrentDate() {
    if (this.selectedDates.length > 0) {
      // calendar expects the first day of the month to be the current date
      let currentDate = this._getDateStringFromMonth(this.selectedDates[0], 0);
      this.currentDate = currentDate;
    } else {
      this.startDate = this.getStringFromDate(this.getDateObject(this.startDate));
      // use client provided current date if exists and it is greater than the current/start date
      // else, use the current/today date
      if (this.currentDateOnLoad && this.currentDateOnLoad > this.startDate) {
          this.currentDate = this.currentDateOnLoad;
      } else {
        this.currentDate = this.startDate;
      }
    }
  }

  _getAllowDisabledFocus(disabled, allowDisabledFocus, unavailable, allowUnavailable) {
    return (disabled && allowDisabledFocus) ||
      (unavailable && allowUnavailable);
  }

  /**
   * @desc Get date string for initial date + number of months
   *
   * @param {String} initialDateString
   * @param {Number} addMonth
   * @return {String}
   */
  _getDateStringFromMonth(initialDateString, addMonth) {
    let newDate = this.getDateObject(initialDateString);

    newDate.setDate(1);
    newDate.setMonth(newDate.getMonth() + addMonth);
    return this.getStringFromDate(newDate);
  }

  /**
   * @desc Check to see if small calendar should be shown
   * @param {String} adaptive
   * @return {Boolean}
   */
  _showSmallCalendar(adaptive) {
    return adaptive === 'xs';
  }

  /**
   * @desc get navigation type for calendar
   * Always return 'tap' if useGridDisplayStyle is false.
   * If useGridDisplayStyle is true, only return 'tap' if
   * adaptive is 'xs', else return 'none'.
   * @param {String} adaptive
   * @param {Boolean} useGridDisplayStyle
   * @return {String} navigation
   */
  _getCalendarNavigation(adaptive, useGridDisplayStyle) {
    return useGridDisplayStyle && adaptive !== 'xs' ? 'none' : 'tap';
  }

  /**
   * @desc Return flag to indicate if nav button should be disabled or not
   *  - nav buttons disabled/hidden when large screen and useGridDisplayStyle is used
   *  - nav buttons disabled/hidden for calendars not at edge of display (not end or begining month)
   *  - nav buttons always enabled for small screens (only one calendar month showing)
   * @param {Number} index index of calendar month
   * @param {String} type type of nav button 'prev' or 'next'
   * @param {String} adaptive
   * @param {Number} monthsToDisplay number of months to display on page
   * @param {Boolean} useGridDisplayStyle flag to indicate if grid display used (no nav buttons on large screens)
   * @return Boolean
   */
  _isNavDisabled(index, type, adaptive, monthsToDisplay, useGridDisplayStyle) {
    return useGridDisplayStyle ? !this._showSmallCalendar(adaptive) :
      !this._showSmallCalendar(adaptive) && (
        (index !== 0 && type === 'prev') || (index !== monthsToDisplay - 1 && type === 'next')
      );
  }

  _getStartDate(index, currentStartDate, calendarDates) {

    return (
      index == 0 && calendarDates && calendarDates.start && calendarDates.start < currentStartDate
    ) ? calendarDates.start : currentStartDate;
  }

  /**
   * @desc Evaluates the isPriceView param to identify the type of calendar we're presenting
   * @param {Boolean} priceView indicates if the calendar is presenting in Prices mode
   * @return {String} `price-view` if `isPriceView` is true, `generic-view` otherwise.
   */
  _getCalendarType(isPriceView) {
    return isPriceView === true ? CALENDAR_TYPE.priceView : CALENDAR_TYPE.genericView;
  }

  /**
   * @desc Get available dates for current calendar view
   *   - Note that all dates need to be slotted in to be selectable
   * @todo Support other types of date in the future using this.dateType
   *
   * @param {Object} availableDates
   * @param {String} currentStartDateString
   * @return {Object[]}
   */
  _getAvailableDates(availableDates, currentStartDateString) {

    if (!availableDates) {
      return;
    }

    let returnList = [];
    const newDate = this.getDateObject(currentStartDateString, false, this._useLocaleDateFormat);

    // loop through each day of current month to slot in all calendar dates to be selectable
    for (let i = newDate.getDate(); i <= this._getNumberOfDays(newDate); i++) {
      newDate.setDate(i);
      const dateString = this.getStringFromDate(newDate);
      const dateToAdd = COMCalendarDateFormatter.getAvailDate(
        dateString,
        availableDates,
        this.dateTypeMap,
        this.isVariableParks,
        this.unblockFutureDates,
        this.setCalendarStylesToBlocked
      );
      returnList.push(dateToAdd);
    }

    return returnList;
  }

  /**
   * @desc Get available dates the entire calendar (all dates provided by availability)
   *
   * @param {Object} availableDates all dates with availability info on the calendar
   * @return {Array} dates with availability info
   */
  _getAvailableDatesAllMonths(availableDates) {
    if (!availableDates) {
      return;
    }

    return Object.values(availableDates).map(date => {
      const currentAvail = COMCalendarDateFormatter.getAvailDate(
        date.date,
        availableDates,
        this.dateTypeMap,
        this.isVariableParks,
        this.unblockFutureDates,
        this.setCalendarStylesToBlocked
      );
      return currentAvail;
    });
  }

  /**
   * @desc Get starting dates of each month for current monthsToDisplay
   *
   * @param {String} currentDate
   * @return {String[]}
   */
  _getCalendarDates(currentDate, adaptive, dates) {

    if (!currentDate) return;
    let calendarDates = this._getMonthsList(adaptive, dates);

    // store start dates into array
    return calendarDates.map(idx => {
      let dateString = this._getDateStringFromMonth(currentDate, idx);
      if (this.startDate > dateString) {
        dateString = this.startDate;
      }
      return dateString;
    });
  }

  /**
   * @desc Get list of months to iterate to generate calendar dates
   * @return {Number[]}
   */
  _getMonthsList(adaptive, dates) {
    let monthsList = [];

    // set initial array with months length
    for (let i = 0; i < (adaptive === 'xs' ? 1 : this.monthsToDisplay); i++) {
      monthsList.push(i);
    }

    // when getting an empty array we only have to show that the dates they are looking for are not available
    return this.showEmptyMessageOnEmptyDates && dates.length === 0 ? [0] : monthsList;
  }

  /**
   * @desc Get Additional Date label content
   * @param {*} dateItem
   * @param {Boolean} priceView
   */
  _getAdditionalDateLabel(dateItem, priceView) {
    let priceOrDisabledLabel = !dateItem.unavailable ? this._getPricePerDayLabel(dateItem.price, priceView) : ', ' + this.ariaContent.dateUnavailable;
    if (this.enableNavToDisabledDates) {
      priceOrDisabledLabel = '';
    }
    return (!priceView && dateItem && dateItem.dateType && dateItem.dateType.a11yContent ? dateItem.dateType.a11yContent + ' ' : '') + priceOrDisabledLabel;
  }

  /**
  * @desc get pricePerDayLabel
  * @param {Object[]} dates
  * @param {Object} date
  * @param {Boolean} showTotalPrice
  * @param {Boolean} hidePricing
  * @return {String}
  */
  _getPricePerDayLabel(price, priceView) {
    if (!priceView || !price) return '';
    this.$.price.value = price;

    return ', ' + this.$.price.textAria;
  }

  _showPrice(dateItem, priceView) {
    return priceView && dateItem.price && !dateItem.unavailable;
  }

  /**
   * @desc Returns the expected endDate of the calendar display
   *       - if displayStrictNumMonths flag is set, return date at the end of the monthsToDisplay range
   *       -Else Get the last date of dates and determine the endDate of the calendar.
   *       Falls back to this.endDate if dates are not defined.
   *       (i.e., ticket mod flow requires dates to be emptied when flex option is selected to disable
   *        any days from being selected)
   * @param {Object[]} dates
   * @param {String} endDate
   * @param {Number} monthsToDisplay
   * @param {Boolean} displayStrictNumMonths
   * @return {String}
   */
  _getEndDate(dates, endDate, monthsToDisplay, displayStrictNumMonths) {
    let calculatedEndDate = '';
    if (!dates && !this.endDate) {
      return '';
    }
    // if we want to show specific number of months despite dates provided
    if (displayStrictNumMonths && !!monthsToDisplay) {
      let today = this.getStringFromDate(this.getToday());
      let lastDate = this.getDateObject(today);
      let month = lastDate.getMonth() + (monthsToDisplay - 1); // subtract 1 for the current month
      let endYear = lastDate.getFullYear();
      calculatedEndDate = this.getStringFromDate(new Date(endYear, month, 1));
    } else {
      // default lastDate to endDate if sets
      let lastDate = endDate;
      if (dates && dates.length > 0) {
        dates = dates.sort((a, b) => {
          return this.getDateObject(a.date) - this.getDateObject(b.date);
        });
        lastDate = dates[dates.length - 1].date;
      }
      lastDate = this.getDateObject(lastDate);
      // the first day of the next month will be the end date

      let month = dates.length === 0 && this.showEmptyMessageOnEmptyDates ? lastDate.getMonth() : (lastDate.getMonth() + 1);
      let year = lastDate.getFullYear();
      calculatedEndDate = this.getStringFromDate(new Date(year, month, 1));
    }
    return calculatedEndDate;
  }

  /**
   * @desc Show drawer if drawerContent is present, drawerEnable is true
   * and if the calendar index is 0 in case we are displaying 2 calendars
   * side by side.
   * @param {Object[]} drawerContent
   * @param {Boolean} drawerEnable
   * @param {Number} index
   * @return {Boolean}
   */
  _showDrawer(drawerContent, drawerEnable, index) {
    return drawerContent && drawerEnable && this._isSingleDayMode() && index === 0;
  }

  /**
   * @desc Send event on selected date
   * @param {String} selectedDate
   */
  _onSelectedDateChanged(selectedDate) {
    this.dispatchEvent(new CustomEvent('com-calendar.selectedDate', {
      bubbles: true,
      composed: true,
      detail: {
        selectedDate: selectedDate
      }
    }));
  }

  /**
   * @desc Toggle drawer based on the calendar id and if
   * selectedDates are present when we are displaying 2 calendars
   * side by side.
   * @param {Array} selectedDates
   */
  _toggleDrawerState(selectedDates) {
    let baseCalendars = this.shadowRoot.querySelectorAll('com-calendar-base');
    baseCalendars.forEach(calendar => {
      calendar.drawerOpen = false;
      if (selectedDates && selectedDates.length > 0 && calendar.id.includes('0')) {
        calendar.drawerOpen = true;
      }
    });
  }

  /**
   * @desc Add 'rowEnd' id to help break calendar months in grid view into rows
   * of specified size
   * @param {Boolean} useGridDisplayStyle
   * @param {Number} monthsPerDisplayRow number of months per grid row
   * @param {Number} monthIndex index value of month inside grid
   * @return {String}
   */
  _getFlexBreakValue(useGridDisplayStyle, monthsPerDisplayRow, monthIndex) {
    if (useGridDisplayStyle && monthsPerDisplayRow != 0) {
      return ((monthIndex + 1) % monthsPerDisplayRow) === 0 ? 'rowEnd' : '';
    }
  }

  /**
   * @desc Calendar legend should appear above the calendar for wdw,
   * otherwise it has default position
   */
  _setCalendarLegendPosition(calendarLegendOnTop) {
    if (calendarLegendOnTop === true) {
      this.moveLegendFromDefaultPosition = true;
    }
  }

  /**
  * @desc notify client when availability data has been loaded
  */
  _dispatchAvailLoad(event) {
    this.dispatchEvent(
      new CustomEvent(
        'com-calendar.avail-data-loaded', {
        bubbles: true,
        composed: true,
        detail: { type: 'avail-data-loaded' }
      }
      )
    );
  }

  /**
   * @desc Returns the Day from a provided date String
   * @param {String} date
   * @return Date
   */
  _formatDateFromString(date) {
    let d = date.split('-');
    date = new Date(d[0], d[1] - 1, d[2]);
    return date.getDate();
  }

  /**
   * @desc function to set the value for useSameCalendarHeight flag
   * works only with small screens up (adaptive > xs), monthsToDisplay = 2 & disabled useGridDisplayStyle flag.
   * @param {String} adaptive
   * @param {Number} monthsToDisplay
   * @param {Boolean} useSameCalendarHeight
   * @return {Boolean}
   */
  _shouldUseSameCalendarHeight(adaptive, monthsToDisplay, useSameCalendarHeight) {
    return useSameCalendarHeight && monthsToDisplay == 2 && adaptive !== 'xs';
  }

  /**
   * @desc Returns class name based on a toggle value
   * @param {Boolean} toggle
   * @param {String} additionalClass
   * @return {String}
   */
  _getAdditionalClass(toggle, additionalClass) {
    return toggle ? additionalClass : '';
  } 

  /**
   * @desc set navigation arrows' disabled states based on startDate and endDate
   * @param {Date} currentDate
   * @param {String} startDate, ex: 2017-10-01
   * @param {String} endDate, ex: 2017-12-31
   * @param {Boolean} disableNavigation 
   * @param {Boolean} disablePrevArrow 
   * @param {Boolean} disableNextArrow 
   */
  _setNavigation(currentDate, startDate, endDate, disableNavigation, disablePrevArrow, disableNextArrow) {
    afterNextRender(this, () => {
      const calendarWrapper = this.shadowRoot.getElementById('calendarWrapper');
      const prevArrow = calendarWrapper.querySelector('#prevArrow');
      const nextArrow = calendarWrapper.querySelector('#nextArrow');
      if (!currentDate) return;

      let ariaNextMonthText = this.ariaContent.navigationNextMonth,
        ariaPreviousMonthText = this.ariaContent.navigationPrevMonth,
        ariaDisabledText = this.ariaContent.navigationDisabled;

      let allowPrev = this._allowPrev(currentDate, startDate, disableNavigation, disablePrevArrow);
      let allowNext = this._allowNext(currentDate, endDate, disableNavigation, disableNextArrow);

      prevArrow.disabled = !allowPrev;
      nextArrow.disabled = !allowNext;

      const prevMonthText = allowPrev ? ariaPreviousMonthText : ariaPreviousMonthText + ' ' + ariaDisabledText;
      const nextMonthText = allowNext ? ariaNextMonthText : ariaNextMonthText + ' ' + ariaDisabledText;

      prevArrow.setAttribute('aria-label', prevMonthText);
      nextArrow.setAttribute('aria-label', nextMonthText);

      this.endDate = this._getEndDate(this.dates, this.endDate, this.monthsToDisplay, this.displayStrictNumMonths);

    })
  }
  /**
   * @desc check if we should show navigation arrows
   * @param {String} adaptive
   * @param {Boolean} useGridDisplayStyle
   * @return {Boolean}
   */
  _hasNavigationArrows(adaptive, useGridDisplayStyle) {
    const navigation = this._getCalendarNavigation(adaptive, useGridDisplayStyle)
    return ['tap', 'swipe-and-tap'].indexOf(navigation) !== -1;
  }

  /**
   * @desc Prevent firing focus on click
   * https://stackoverflow.com/questions/8735764/prevent-firing-focus-event-when-clicking-on-div/45131490#45131490
   * @param {Event} e
   */
  _preventFocus(e) {
      if (e.target.getAttribute('allow-disabled-focus')) return;
      e.preventDefault();
  }
  /**
   * @desc determine if previous operation is allowed
   * @param {Date} currentDate
   * @param {String} startDate
   * @param {Boolean} disableNavigation
   * @param {Boolean} disablePrevArrow
   * @return {Boolean}
   */
  _allowPrev(currentDate, startDate, disableNavigation, disablePrevArrow) {
    if (disableNavigation || disablePrevArrow) return false;

    let useStartDate = (this.prevStartDate) ? this.prevStartDate : startDate;
    const isSameDate = this.isSameDate(currentDate, useStartDate);

    return currentDate > useStartDate && !isSameDate;
  }
   /**
   * @desc previous arrow handler
   * @param {Event} e
   */
  _prevHandler(e) {
    if (!this._allowPrev(this.currentDate, this.startDate, this.disableNavigation, this.disablePrevArrow)) {
      if (this.propogateNavEvents) {
        this._navigationEventHandler(e, 'previous');
      }
      return;
    }

    if (this.navigation === 'tap' && this.animate !== false) {
      this._tapAnimation('previous');
    } else {
      let month = this.getDateObject(this.currentDate);
      month.setDate(1);
      month.setMonth(month.getMonth() - 1);
      this.currentDate = this.getStringFromDate(month);
    }

    this._navigationEventHandler(e, 'previous');
  }
  /**
   * @desc determine if next operation is allowed
   * @param {String} currentDate
   * @param {String} endDate
   * @param {Boolean} disableNavigation
   * @param {Boolean} disableNavigation
   * @return {Boolean}
   */
  _allowNext(currentDate, endDate, disableNavigation, disableNextArrow) {
    // here we need to take last month currenDate from the calendar (ie: december - january, currentDate would be for january to get the next month)
    currentDate = this._getCalendarDates(this.currentDate, this.adaptive, this.dates).pop();
    if (disableNavigation || disableNextArrow) return false;
    const isSameDate = this.isSameDate(currentDate, endDate);
    if (!endDate) return true;

    return currentDate < endDate && !isSameDate;
  }
  /**
   * @desc `_navigationEventHandler` dispatch custom event to parent
   * @param {Event} event
   * @param {String} direction // Retreat or Advance
   * @return {Void}
   */
  _navigationEventHandler(event, direction) {
    this.dispatchEvent(new CustomEvent('com-calendar.navigation-change', {
      bubbles: true,
      composed: true,
      detail:{
        target: event.target,
        direction: direction,
        currentDate: this.currentDate
      }
    }));
  }

  /**
   * @desc next arrow handler
   * @param {Event} e
   */
  _nextHandler(e) {
    // here we need to take last date from the calendar (ie: december - january, current date would be for january to get the next month)
    let currentDate = this._getCalendarDates(this.currentDate, this.adaptive, this.dates).pop();
    this.nextMonthOffset = this._getNextMonthOffset(this.adaptive, this.monthsToDisplay);

    if (!this._allowNext(this.currentDate, this.endDate, this.disableNavigation, this.disableNextArrow)) {
      if (this.propogateNavEvents) {
        this._navigationEventHandler(e, 'previous');
      }
      return;
    }

    if (this.navigation === 'tap' && this.animate !== false) {

      this._tapAnimation('next');
    } else {
      let month = this.getDateObject(currentDate);
      month.setMonth(month.getMonth() + 2 + this.nextMonthOffset, 0);

      if (this._showSmallCalendar(this.adaptive) || this.monthsToDisplay < 2) {
        this.currentDate = this.getStringFromDate(month);
      }
    }

    if (!e.detail || !e.detail.disableEventHandle) {
      this._navigationEventHandler(e, 'next');
    }

  }
}
window.customElements.define(COMCalendar.is, COMCalendar);
